import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from "react-router-dom";
import { getPermissions, getUser, getConfigurableApi } from "@/api/app";
import { useSelector } from "react-redux";

import { ConfigProvider, theme, App as AntApp } from "antd";
import zhCN from "antd/locale/zh_CN";
import zhTW from "antd/locale/zh_TW";
import enUS from "antd/locale/en_US";

import fp from "lodash/fp";

import "src/normalize.css";
import "src/App.css";
import "src/global.less";
import routerConfig from "src/router";
import useResize from "./hooks/useResize";

import { useDispatch } from "react-redux";
import * as types from "@/store/action-types";
import Loading from "@/components/Loading";

// 多语言
import { IntlProvider } from "react-intl";
import zhCNTranslation from "./i18n/zh-CN.json";
import zhTWTranslation from "./i18n/zh-TW.json";
import enUSTranslation from "./i18n/en-US.json";

import "./assets/douyinmeihao.otf";

// 定义应用支持的语言和默认语言
const localeData = {
  "zh-CN": zhCNTranslation,
  "zh-TW": zhTWTranslation, // 港澳繁体
  "en-US": enUSTranslation,
};

// 定义组件安装包
const antdLocaleData = {
  "zh-CN": zhCN,
  "zh-TW": zhTW,
  "en-US": enUS,
};

export const toPermissions = fp.flow(fp.pickBy(fp.size), fp.keys); //只留菜单权限

export function withAuth(Component, extraProps) {
  const { access, path } = extraProps;
  return (props) => {
    const auth = useSelector((state) => state.auth);
    const { themeStatus } = useSelector((state) => state.contentCheck);
    if (path === "/login") {
      return <Component {...props} />;
    }
    if (auth.user === undefined)
      return (
        <div
          className={`${
            themeStatus == "sun"
              ? "fullpage-loading-wrap"
              : "fullpage-loading-wrap-drak"
          }`}
        >
          <Loading />
        </div>
      );
    if (auth.user === null) return <Redirect to="/login" />;

    if (path === "/") {
      const menuItems = auth.menuItems;
      if (!menuItems) return null;
      const redirectUrl = menuItems[0].children
        ? menuItems[0].children[0].url
        : menuItems[0].url;
      return <Redirect to={redirectUrl} />;
    } else {
      const permissions = [
        ...toPermissions(auth.permissions),
        auth.user?.is_admin ? "admin" : "_normal",
      ];
      if ((access && permissions.includes(access)) || !access)
        return <Component {...props} />;
    }
  };
}

function App() {
  const zoom = useResize();
  const dispatch = useDispatch();

  const [locale, setLocale] = useState({
    locale: "zh-CN",
    localeData: "zh-CN",
  });

  const { configsPermissions, configsDisplay } = useSelector(
    (state: any) => state.auth
  );
  const { themeStatus } = useSelector((state: any) => state.contentCheck);
  useEffect(() => {
    dispatch({ type: types.RESET_ZOOM, data: zoom });
  }, [zoom]);

  useEffect(() => {
    const init = async () => {
      let permissionData: any = null;
      let userData: any = null;

      if (!configsPermissions || !configsDisplay) {
        try {
          const { data, code } = await getConfigurableApi();

          if (code === 0) {
            const { locale, system_name } = data.display;
            setLocale({
              // locale: locale,
              // localeData: locale === 'zh-TW' ? "tw" : (locale === 'zh-CN' ? "cn" : "en")
              locale: locale,
              localeData: locale,
            });

            document.title = system_name;

            localStorage.setItem("locale", locale);

            // 测试 data.permissions.diffusion = [0]
            dispatch({
              type: types.CONFIGURABLE_DATA,
              data: {
                ...data,
                locale: locale,
              },
            });
          }

          // if(code === 0) {
          //   let {locale, system_name} = data.display
          //   locale = "zh-CN"
          //   setLocale({
          //     locale: locale,
          //     localeData: locale
          //   })

          //   document.title = system_name

          //   localStorage.setItem("locale", locale);

          //   // 测试 data.permissions.diffusion = [0]
          //   dispatch({
          //     type: types.CONFIGURABLE_DATA,
          //     data: {
          //       ...data,
          //       locale: locale
          //     }
          //   });
          //  }
        } catch (e) {
          console.error(e);
        }
      }

      try {
        permissionData = (await getPermissions()).data;
      } catch (e) {
        console.error(e);
      }
      try {
        userData = (await getUser()).data;
      } catch (e) {
        console.error(e);
        dispatch({ type: types.USER_FAIL });
      }
      if (userData) {
        dispatch({ type: types.USER_SUCCESS, data: userData });
      }
      if (permissionData) {
        dispatch({ type: types.PERMISSIONS, data: { ...permissionData } });
      }
    };
    init();
  }, [dispatch]);

  return (
    <ConfigProvider
      locale={antdLocaleData[locale.locale]}
      // locale={zhTW}
      theme={{
        algorithm:
          themeStatus == "sun" ? theme.defaultAlgorithm : theme.darkAlgorithm,
        token: {
          colorPrimary: "#007FF5",
        },
        components: {
          Input: {
            // 默认使用的边框颜色
            colorBorder: "rgba(0,122,245,0.7)",
            // 圆角大小
            borderRadius: 8,
            // 控件的高度
            controlHeight: 36,
            // 控制占位文本的颜色
            colorTextPlaceholder: "rgba(255,255,255,0.50)",
            // 失败边框颜色
            colorError: "rgba(255,103,103,0.7)",
            // 控制输入组件的外轮廓线宽度
            controlOutlineWidth: 0,
            // 文本颜色
            colorText: "#fff",
            // 悬浮态边框颜色
            colorPrimaryHover: "rgba(67,165,255,0.7)",
            // colorPrimaryActive: '#007AF5',
          },
          Checkbox: {
            colorBgContainer: themeStatus == "sun" ? "" : "#080D1A",
            colorBorder: "rgba(0,122,245,0.7)",
            colorPrimary: "#007FF5",
            colorPrimaryHover: "#43A5FF",
            colorTextDisabled: "rgba(255,255,255,0.50)",
            colorBgContainerDisabled: "#080D1A",
          },
          Select: {
            colorBorder: "rgba(0,122,245,0.7)",
            colorPrimaryHover: "rgba(67,165,255,0.7)",
            controlItemBgHover: "rgba(0,127,245,0.30)",
            controlItemBgActive: "rgba(0, 127, 245, 0.5)",
            colorTextPlaceholder: "rgba(255,255,255,0.50)",
            colorTextQuaternary: "#007FF5",
          },
          DatePicker: {
            colorBorder: "rgba(0,122,245,0.7)",
          },
        },
      }}
    >
      <AntApp>
        <IntlProvider
          locale={locale.locale}
          messages={localeData[locale.localeData]}
        >
          <Router>
            <Switch>
              {useMemo(
                () =>
                  routerConfig.map((route, index) => {
                    const ActualRoute = withAuth(Route, {
                      access: route.access,
                      path: route.path,
                    });
                    return (
                      <ActualRoute
                        key={index}
                        path={route.path}
                        exact={route.exact}
                        component={route.component}
                      />
                    );
                  }),
                []
              )}
              <Redirect to="/" />
            </Switch>
          </Router>
        </IntlProvider>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
